import { useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { Flex, Text, TextField } from '@dodilio/ui-design-lib';
import { passwordChecks } from '../password';

/**
 * Choosing a password: one field (nobody among the big platforms asks to
 * type it twice), an eye to see it, and the rules as a checklist that ticks
 * as they're met, shown before the first keystroke.
 */
export const NewPassword = ({ field, value, error, label = 'Password', autoFocus }: {
    field: UseFormRegisterReturn;
    value: string;
    error?: string;
    label?: string;
    autoFocus?: boolean;
}) => {
    const [shown, setShown] = useState(false);
    const checks = passwordChecks(value ?? '');
    return (
        <Flex direction="column" gap="2">
            <TextField.Root
                {...field}
                type={shown ? 'text' : 'password'}
                autoComplete="new-password"
                autoFocus={autoFocus}
                size="2"
                variant="surface"
                aria-label={label}
                aria-describedby="password-rules"
                color={error ? 'tomato' : 'gray'}
            >
                <TextField.Slot className="fieldLabel"><Text size="1">{label}</Text></TextField.Slot>
                <TextField.Slot side="right">
                    <button
                        type="button"
                        onClick={() => setShown((v) => !v)}
                        aria-label={shown ? 'Hide password' : 'Show password'}
                        aria-pressed={shown}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--gray-11)', font: 'inherit', fontSize: 12, padding: 0 }}
                    >
                        {shown ? 'Hide' : 'Show'}
                    </button>
                </TextField.Slot>
            </TextField.Root>
            <ul id="password-rules" aria-live="polite" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
                {checks.map((c) => (
                    <li key={c.label}>
                        <Text size="1" color={c.met ? 'green' : 'gray'}>
                            <span aria-hidden style={{ display: 'inline-block', width: 14 }}>{c.met ? '✓' : '·'}</span>
                            {c.label}
                            <span className="sr-only">{c.met ? ' (done)' : ' (not yet)'}</span>
                        </Text>
                    </li>
                ))}
            </ul>
            {error && <Text size="1" className="field-error" role="alert">{error}</Text>}
        </Flex>
    );
};
